import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";
import { Commet } from "react-loading-indicators";

// Set the dimensions and margins of the graph
const margin = { top: 30, right: 100, bottom: 50, left_split: 200, left: 100 };

const Toggle = ({ split, onChange }) => {
  return (
    <div style={{ width: "350px" }}>
      <div className="bubbleToggleContainer">
        <label
          className={`bubbleToggle ${split ? "bubbleToggleSelected" : ""}`}
          style={{
            backgroundColor: split ? "#003C73" : "white",
            color: split ? "white" : "#003C73",
            fontFamily: "National2",
            width: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input
            name="split"
            type="radio"
            value="1"
            defaultChecked={split}
            onClick={() => onChange(true)}
            style={{ display: "none" }}
          />
          <small>By Building</small>
        </label>
        <label
          className={`bubbleToggle ${!split ? "bubbleToggleSelected" : ""}`}
          style={{
            backgroundColor: !split ? "#003C73" : "white",
            color: !split ? "white" : "#003C73",
            fontFamily: "National2",
            width: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input
            name="split"
            type="radio"
            value="0"
            defaultChecked={!split}
            onClick={() => onChange(false)}
            style={{ display: "none" }}
          />
          <small>Campus-Wide</small>
        </label>
      </div>
    </div>
  );
};

const EnergyIndicator = () => {
  return (
    <svg width="200" height="100">
      <circle cx="20" cy="56" r="10" fill="#EEAC28" />
      <circle cx="45" cy="53" r="15" fill="#EEAC28" />
      <circle cx="75" cy="51" r="17" fill="#EEAC28" />
      <circle cx="110" cy="50" r="20" fill="#EEAC28" />
      <circle cx="150" cy="47" r="23" fill="#EEAC28" />
      <text x="30" y="90" fontSize="15" fill="#000">
        Energy Use (Btu) →
      </text>
    </svg>
  );
};

const TimeLegend = () => {
  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <div>
          <div style={{ width: "200px", height: "15px", position: "relative" }}>
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(to right, #B2D27F, #FFA00F)",
                borderRadius: "5px",
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Bubblechart2 = ({ width, height }) => {
  const graphDataTemp = useAppSelector((state) => state.waterKPI).data;
  const ref = useRef();
  const [split, setSplit] = useState(true);

  // Specify the chart’s dimensions.
  useEffect(() => {
    let simulation;

    if (graphDataTemp.length === 0) {
      const retryTimeout = setTimeout(() => {
        // Call the useEffect again after 10 seconds
        clearTimeout(retryTimeout);
      }, 10000); // 10 seconds
    }

    function drawChart() {
      const deepCloneData = (data) => {
        return data.map((item) => ({
          ...item,
          timestamp: new Date(item.timestamp),
        }));
      };

      const graphData = deepCloneData(graphDataTemp);

      if (graphData.length === 0) {
        const retryTimeout = setTimeout(() => {
          clearTimeout(retryTimeout);
        }, 10000);
        return; // Exit early if no data
      }

      // Clear old data
      d3.select(ref.current).selectAll("*").remove();

      if (!Array.isArray(graphData)) {
        console.error("Invalid data format:", graphData);
        return;
      }

      const buildings = new Set(graphData.map((d) => d.buildingName));
      console.log(buildings);

      let nodes;

      if (split) {
        // If split by building, create nodes for each building
        nodes = Array.from(buildings).map((building) => ({
          building,
          values: graphData.filter((d) => d.buildingName === building),
        }));
      } else {
        // Otherwise, use all data as one node
        nodes = [{ building: "Campus Wide", values: graphData }];
      }

      const x = d3
        .scaleUtc()
        .domain(d3.extent(graphData, (d) => d.timestamp))
        .range([split ? margin.left_split : margin.left, width - margin.right]);

      const y = d3
        .scaleBand()
        .domain(split ? Array.from(buildings) : ["Campus Wide"])
        .range([height - margin.bottom, margin.top + 100]);

      // Define the time format for the ticks on the x-axis
      const timeFormat = d3.timeFormat("%Y");

      const xAxis = (g) =>
        g
          .attr("transform", `translate(0, ${margin.top + 20})`)
          .call(d3.axisTop(x).tickFormat(timeFormat).ticks(5))
          .call((g) =>
            g
              .append("text")
              .attr("x", margin.left - 20)
              .attr("y", 0)
              .attr("fill", "black")
              .style("text-anchor", "end")
              .style("font-size", "15px")
              .style("font-family", "National2")
              .text("Time")
          )
          .call((g) =>
            g
              .selectAll(".tick text")
              .style("font-size", "15px")
              .style("font-family", "National2")
          );

      const yAxis = (g, scale = y, ticks = y.domain()) =>
        g
          .attr("transform", `translate(${0}, 10)`)
          .call(
            d3.axisLeft(scale).tickFormat((d) => {
              // Mapping tick labels to the desired names
              switch (d) {
                case "Class of 1953 Commons":
                  return "FOCO";
                case "Engineer and Comp Science Ctr":
                  return "Engineering Ctr";
                case "Baker, Berry, Carson Library - Complex":
                  return "Baker Library";
                case "Class of 1978 Life Sciences Center":
                  return "Life Sciences Ctr";
                case "Rockefeller Ctr for the Social Sciences":
                  return "Rockefeller Ctr";
                case "Burke Chemistry Laboratory":
                  return "Burke Lab";
                default:
                  return d;
              }
            })
          )
          .call((g) => g.style("text-anchor", "start"))
          .call((g) => g.select(".domain").remove())
          .call((g) =>
            g
              .selectAll(".tick text")
              .style("font-size", "20px")
              .style("font-family", "National2")
          )
          .call((g) => g.selectAll(".tick line").remove());

      const r = d3
        .scaleSqrt()
        .domain(d3.extent(graphData, (d) => d.value))
        .range([6, 20]);

      const colorScale = d3
        .scaleSequential()
        .domain([0, graphData.length - 1])
        .interpolator(d3.interpolateHclLong("#0055A3", "#FFA00F")); // Color interpolation scheme
      //.range(["#0055A3", "#FFA00F"]); // Adjusted color range

      const svg = d3
        .select(ref.current)
        .attr("viewBox", [0, 0, width, height + margin.top + margin.bottom]);

      svg.append("g").call(xAxis);
      if (split) {
        svg.append("g").call(yAxis);
      }

      const circles = svg
        .append("g")
        .selectAll("circle")
        .data(graphData)
        .join("circle")
        .attr("cx", (d) => x(d.timestamp))
        .attr(
          "cy",
          (d) => y(split ? d.buildingName : "Campus Wide") + y.bandwidth() / 2
        )
        .attr("r", (d) => r(d.value))
        .attr("fill", (d, i) => colorScale(i));

      simulation = d3
        .forceSimulation(graphData)
        .force("x", d3.forceX((d) => x(d.timestamp)).strength(split ? 1 : 0.1))
        .force(
          "y",
          d3
            .forceY(
              (d) =>
                y(split ? d.buildingName : "Campus Wide") + y.bandwidth() / 2
            )
            .strength(split ? 1 : 0.1)
        )
        .force("collide", d3.forceCollide((d) => r(d.value)).strength(1))
        .on("tick", () => {
          circles.attr("cx", (d) => d.x).attr("cy", (d) => d.y);
        });

      if (!split) {
        simulation.force("charge", d3.forceManyBody().strength(-10));
      }

      return () => simulation.stop();
    }
    drawChart();
  }, [graphDataTemp, height, split, width]);

  const handleToggle = (isSplit) => {
    setSplit(isSplit);
  };

  return graphDataTemp.length === 0 ? (
    <Commet color="#32cd32" size="medium" text="" textColor="" />
  ) : (
    <div>
      <div className="bubbleChartBox">
        <svg width={width} height={height} id="bubblechart" ref={ref} />
        <div className="bcInfoBox">
          <Toggle onChange={handleToggle} split={split} />
        </div>
        <div className="bcEnergyTimeBox">
          <EnergyIndicator />
          <TimeLegend />
        </div>
      </div>
    </div>
  );
};
export default Bubblechart2;
